<template>
  <form class="space-y-4" @submit.prevent="submit">
    <p v-if="!workshopAppointment" class="text-2xl font-medium">
      {{ t('createWorkshopAppointment') }}
    </p>

    <div v-if="workshopAppointment" class="grid gap-4 md:grid-cols-3">
      <div class="rounded-md bg-white p-4 md:col-span-2">
        <div class="flex items-center gap-4">
          <figure class="basis-1/4 self-center md:basis-[20%]">
            <img v-if="carImageUrl" :src="carImageUrl" />
          </figure>
          <div class="flex flex-1 flex-col gap-2 md:flex-row md:gap-4">
            <div class="flex basis-[60%] flex-col gap-2">
              <p class="grid items-center font-semibold md:flex md:gap-2">
                <span>
                  <LicencePlate v-if="!car.isGhostCar">{{
                    car.licencePlate || 'X-XX XXXX'
                  }}</LicencePlate>
                </span>
                <span>{{ car.vehicleType?.make.name }} {{ car.vehicleType?.model }}</span>
              </p>
              <p class="flex items-center gap-1">
                <MapPinIcon class="size-4 text-obsidian-900 opacity-30" />
                <span>{{ currentStation ? getLocalizedField(currentStation.info) : '-' }}</span>
              </p>
            </div>
            <div class="flex basis-[40%] flex-col gap-2">
              <p v-if="!car.isGhostCar" class="flex items-center gap-1">
                <ScanBarcodeIcon class="size-4 text-obsidian-900 opacity-30" />
                <span>{{ car.fin || '-' }}</span>
              </p>
              <p class="flex items-center gap-1">
                <PaintbrushVerticalIcon class="size-4 text-obsidian-900 opacity-30" />
                <span>{{ car.color ? t(`carColors.${car.color}`) : '-' }}</span>
              </p>
            </div>
          </div>
        </div>
      </div>
      <div class="rounded-md bg-white p-4">
        <div class="flex items-start gap-2">
          <div class="flex-1 space-y-1">
            <p class="text-sm text-hazel-700">{{ t('allocationStatus') }}</p>
            <AllocationStatusBadge
              :status="formValues.allocationStatus ?? AllocationStatus.CONFIRMED"
              size="md"
            />
            <Badge
              v-if="workshopAllocation && !workshopAllocation.isBlocking"
              variant="warning"
              size="md"
            >
              {{ t('doesNotBlockBookings') }}
            </Badge>
          </div>
          <DropdownDotted :buttons="readyForPickupDropdownItems" />
        </div>
        <CVButton
          v-if="showMarkReadyForPickupButton"
          variant="default"
          class="mt-2 w-full"
          :is-loading="isUpdatingWorkshopAppointment"
          :disabled="!canEditWorkshopAppointmentField('readyForPickup')"
          @click.prevent="updateReadyForPickup(true)"
        >
          {{ t('markAsReadyForPickup') }}
        </CVButton>
      </div>
    </div>
    <h2 v-if="workshopAppointment">{{ t('appointmentOverview') }}</h2>
    <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
      <MultiSelectField
        name="types"
        :label="t('types')"
        :options="typeOptions"
        :placeholder="t('selectTypes')"
        :disabled="!canEditWorkshopAppointmentField('types')"
      />
      <template v-if="canEditField('startDate')">
        <BookingRangePicker
          :start-date="formValues.startDate"
          :end-date="formValues.endDate"
          :start-date-title="t('startDate')"
          :end-date-title="t('endDate')"
          :highlighted-dates="isBlockedDate"
          :disabled="!canEditField('endDate')"
          @update:start-date="setFieldValue('startDate', $event)"
          @update:end-date="setFieldValue('endDate', $event)"
        />
      </template>
      <div v-else class="grid grid-cols-1 gap-4 md:grid-cols-2">
        <DatepickerField
          name="startDate"
          :label="t('startDate')"
          enable-time-picker
          :disabled="true"
        />
        <DatepickerField
          name="endDate"
          :label="t('endDate')"
          enable-time-picker
          :disabled="!canEditField('endDate')"
        />
      </div>
      <SelectField
        name="locationType"
        :label="t('location')"
        :options="locationTypeOptions"
        :disabled="!canEditField('pickupLocationType')"
      />
      <SelectField
        v-if="formValues.locationType === BookingLocationType.STATION"
        name="stationId"
        :label="t('station')"
        :options="stationOptions"
        :disabled="!canEditField('pickupResponsibleStation')"
      />
      <SearchLocation
        v-else
        auto-confirm-on-select
        :location="formValues.location"
        :disabled="!canEditField('pickupLocation')"
        @update:location="
          setFieldValue('location', {
            coordinates: $event?.coordinates ?? null,
            address: $event?.address ?? null,
          })
        "
      />
      <InputField
        name="contactPerson"
        :label="t('contactPerson')"
        :disabled="!canEditWorkshopAppointmentField('contactPerson')"
      />
      <div class="grid grid-cols-2 gap-4">
        <InputField
          v-if="workshopAppointment"
          name="milageStart"
          type="number"
          :min="0"
          adornment="km"
          empty-value-to="null"
          :label="t('handoverMileage')"
          :disabled="!canEditField('milageStart')"
        />
        <InputField
          v-if="workshopAppointment"
          name="milageEnd"
          type="number"
          :min="0"
          adornment="km"
          empty-value-to="null"
          :label="t('returnMileage')"
          :disabled="!canEditField('milageEnd')"
        />
      </div>
      <div class="col-span-full">
        <TextareaField
          name="additionalInformation"
          :label="t('additionalInformation')"
          :disabled="!canEditWorkshopAppointmentField('additionalInformation')"
        />
      </div>
      <div>
        <div class="w-fit space-y-3">
          <CheckboxField
            name="usesCollectAndReturnService"
            :disabled="!canEditWorkshopAppointmentField('usesCollectAndReturnService')"
          >
            {{ t('usesCollectAndReturnService') }}
          </CheckboxField>
        </div>
      </div>
      <div v-if="workshopAppointment">
        <h4 class="mb-2 font-medium">{{ t('costs') }}</h4>
        <div class="space-y-2">
          <div
            v-for="(cost, index) in formValues.costs"
            :key="cost.type"
            class="grid grid-cols-1 items-center gap-2 md:grid-cols-[minmax(0,1fr)_220px]"
          >
            <span class="text-sm text-hazel-700">{{
              t(`workshopAppointmentTypes.${cost.type}`)
            }}</span>
            <InputField
              :name="`costs[${index}].cost`"
              type="number"
              :min="0"
              :max-fraction-digits="2"
              adornment="€"
              :disabled="!canEditWorkshopAppointmentField('costs')"
            />
          </div>
          <div
            class="border-primary/20 grid grid-cols-1 items-center gap-2 border-t pt-2 text-sm font-semibold md:grid-cols-[minmax(0,1fr)_220px]"
          >
            <span>{{ t('totalCosts') }}</span>
            <span class="text-right">{{ formatCurrency(totalFormCosts) }}</span>
          </div>
        </div>
      </div>
      <div v-if="workshopAppointment" class="col-span-full">
        <h4 class="mb-2 font-medium">{{ t('documents') }}</h4>
        <WorkshopAppointmentDocuments :workshop-appointment-id="workshopAppointment.id" />
      </div>
    </div>
    <WorkshopDueTasksOverview
      v-if="!workshopAppointment"
      :car="car"
      @open-workshop-appointment="(appointment) => emit('open-workshop-appointment', appointment)"
    />

    <Divider />

    <div class="flex justify-between">
      <CVButton
        v-if="canDeleteWorkshopAppointment"
        variant="danger"
        :is-loading="isDeletingWorkshopAppointment"
        :disabled="!canEditResource || isUpdatingCar"
        @click.prevent="removeWorkshopAppointment"
      >
        {{ t('deleteAppointment') }}
      </CVButton>
      <div v-else />
      <div class="flex gap-2">
        <CVButton
          variant="secondary"
          type="button"
          :disabled="isUpdatingCar"
          @click.prevent="emit('complete')"
          >{{ t('cancel') }}</CVButton
        >
        <CVButton
          variant="default"
          type="submit"
          :is-loading="isUpdatingCar"
          :disabled="!canEditResource"
        >
          {{ workshopAppointment ? t('saveAppointment') : t('createAppointment') }}
        </CVButton>
      </div>
    </div>
  </form>
</template>

<script lang="ts" setup>
import type { Car } from '@/entities/cars/car.entity';
import { useI18n } from 'vue-i18n';
import { computed, toRef, watch } from 'vue';
import { MapPinIcon, PaintbrushVerticalIcon, ScanBarcodeIcon } from 'lucide-vue-next';
import LicencePlate from '@/components/LicencePlate.vue';
import { useCurrentStationOfCar } from '@/hooks/use-current-station-of-car';
import { useLocalizedFields } from '@/hooks/use-localized-fields';
import Divider from '@/components/Divider.vue';
import { useForm } from 'vee-validate';
import * as yup from 'yup';
import { toTypedSchema } from '@vee-validate/yup';
import { dateOrStringRequired } from '@/validation/car.schema';
import BookingRangePicker from '@/views/handover/components/overview/BookingRangePicker.vue';
import { DateTime } from 'luxon';
import {
  type CreateWorkshopAppointmentDto,
  type WorkshopAppointment,
} from '@/entities/workshop-appointments/workshop-appointment.entity';
import {
  useCreateWorkshopAppointment,
  useDeleteWorkshopAppointment,
  useUpdateWorkshopAppointment,
} from '@/queries/use-workshop-appointments';
import MultiSelectField, { type MultiSelectOptionData } from '@/components/MultiSelectField.vue';
import { workshopAppointmentTypeOrder } from '@/constants/workshop-appointment-type-order.constant';
import InputField from '@/components/InputField.vue';
import TextareaField from '@/components/TextareaField.vue';
import CheckboxField from '@/components/CheckboxField.vue';
import SelectField from '@/components/SelectField.vue';
import SearchLocation from '@/components/booking-forms/SearchLocation.vue';
import { useStations } from '@/queries/use-stations';
import { useFlattenPaginatedData } from '@/hooks/use-flatten-paginated-data';
import { useAllocations } from '@/queries/use-allocations';
import { BookingLocationType } from '@/entities/bookings/booking-location-type.enum';
import { pointToLocationDto } from '@/hooks/use-location-to-coordinates';
import type { GeocodedLocation } from '@/entities/bookings/booking.entity';
import WorkshopDueTasksOverview from './WorkshopDueTasksOverview.vue';
import {
  AllocationStatus,
  WorkshopAppointmentType,
  type WorkshopAppointmentCost,
} from '@carvia/ros-client-types';
import DatepickerField from '@/components/DatepickerField.vue';
import { useCanEditField } from '@/hooks/use-can-edit-field';
import WorkshopAppointmentDocuments from './WorkshopAppointmentDocuments.vue';
import { formatCurrency } from '@/utils/format-numbers';
import AllocationStatusBadge from '@/components/badges/AllocationStatusBadge.vue';
import Badge from '@/components/Badge.vue';
import { Alert } from '@/services/alert.service';
import router from '@/router';
import DropdownDotted, { type DropdownItem } from '@/components/DropdownDotted.vue';
import CVButton from '@/components/buttons/CVButton.vue';
import { isCarUnavailableError } from '@/entities/api-error';

const props = defineProps<{
  car: Car;
  workshopAppointment?: WorkshopAppointment;
}>();

const emit = defineEmits<{
  (e: 'complete'): void;
  (e: 'open-workshop-appointment', appointment: WorkshopAppointment): void;
}>();

const { t, locale } = useI18n();
const { getLocalizedField } = useLocalizedFields();

const carImageUrl = computed(() => props.car.imageUrl ?? props.car.vehicleType?.imageUrl ?? null);
const currentStation = useCurrentStationOfCar(toRef(props, 'car'));

const { mutateAsync: createWorkshopAppointment, isPending: isCreatingWorkshopAppointment } =
  useCreateWorkshopAppointment();
const { mutateAsync: updateWorkshopAppointment, isPending: isUpdatingWorkshopAppointment } =
  useUpdateWorkshopAppointment();
const { mutateAsync: deleteWorkshopAppointment, isPending: isDeletingWorkshopAppointment } =
  useDeleteWorkshopAppointment();
const isUpdatingCar = computed(
  () => isCreatingWorkshopAppointment.value || isUpdatingWorkshopAppointment.value,
);
const workshopAllocation = computed(() => props.workshopAppointment?.allocations?.[0]);
const canDeleteWorkshopAppointment = computed(
  () =>
    !!props.workshopAppointment &&
    ![AllocationStatus.HANDED_OVER, AllocationStatus.RETURNED].includes(
      formValues.allocationStatus,
    ),
);
const showMarkReadyForPickupButton = computed(
  () =>
    formValues.allocationStatus === AllocationStatus.HANDED_OVER &&
    !formValues.readyForPickup &&
    canEditWorkshopAppointmentField('readyForPickup'),
);
const canUnsetReadyForPickup = computed(
  () =>
    formValues.readyForPickup &&
    formValues.allocationStatus === AllocationStatus.HANDED_OVER &&
    canEditWorkshopAppointmentField('readyForPickup'),
);
const readyForPickupDropdownItems = computed<DropdownItem[]>(() =>
  canUnsetReadyForPickup.value
    ? [
        {
          title: t('unsetReadyForPickup'),
          onClick: () => updateReadyForPickup(false),
        },
      ]
    : [],
);
const { data: stationsData } = useStations(500);
const stations = useFlattenPaginatedData(stationsData);
const stationOptions = computed(() => [
  { value: undefined, label: t('notSelected') },
  ...stations.value.map((station) => ({ value: station.id, label: station.info.de })),
]);
const locationTypeOptions = computed(() =>
  Object.entries(BookingLocationType).map(([key, value]) => ({
    label: t(`locationTypes.${key}`),
    value,
  })),
);
interface WorkshopAppointmentFormValues {
  types: WorkshopAppointmentType[];
  costs: WorkshopAppointmentCost[];
  startDate: string;
  endDate: string;
  allocationStatus: AllocationStatus;
  locationType: BookingLocationType;
  stationId?: string;
  location?: GeocodedLocation | null;
  contactPerson: string;
  milageStart: number | null;
  milageEnd: number | null;
  additionalInformation: string;
  usesCollectAndReturnService: boolean;
  readyForPickup: boolean;
}
const typeOptions = computed<MultiSelectOptionData[]>(() =>
  workshopAppointmentTypeOrder.map((value) => ({
    value,
    label: t(`workshopAppointmentTypes.${value}`),
  })),
);

const getDefaultStartDate = () =>
  DateTime.now().set({ hour: 9, minute: 0, second: 0, millisecond: 0 }).toISO() as string;
const getDefaultEndDate = () =>
  DateTime.now().set({ hour: 17, minute: 0, second: 0, millisecond: 0 }).toISO() as string;

const blockingAllocationsQuery = computed(() => ({
  limit: 500,
  filter: [
    {
      carIds: [props.car.id],
      isActive: true,
      isBlocking: true,
      endDateAfter: DateTime.now().minus({ days: 1 }).toJSDate(),
      startDateBefore: DateTime.now().plus({ months: 24 }).toJSDate(),
    },
  ],
}));
const { data: blockingAllocationsData } = useAllocations(blockingAllocationsQuery);
const blockingAllocations = useFlattenPaginatedData(blockingAllocationsData);

const ownAllocationIds = computed(
  () => new Set((props.workshopAppointment?.allocations ?? []).map((a) => a.id)),
);

const blockers = computed(() =>
  blockingAllocations.value
    .filter((a) => !ownAllocationIds.value.has(a.id))
    .map((a) => ({
      start: DateTime.fromISO(a.startDate as unknown as string),
      end: DateTime.fromISO(a.endDate as unknown as string),
    })),
);

const isBlockedDate = computed<(date: Date) => boolean>(() => {
  const ranges = blockers.value;
  return (date: Date) => {
    const dayStart = DateTime.fromJSDate(date).startOf('day');
    const dayEnd = dayStart.plus({ days: 1 });
    return ranges.some((blocker) => blocker.start < dayEnd && blocker.end > dayStart);
  };
});

const normalizeCosts = (
  selectedTypes: WorkshopAppointmentType[],
  costs: WorkshopAppointmentCost[],
): WorkshopAppointmentCost[] => {
  const costsByType = new Map(costs.map((entry) => [entry.type, entry]));
  return selectedTypes.map((type) => ({
    type,
    cost: Number(costsByType.get(type)?.cost ?? 0),
  }));
};

const totalFormCosts = computed(() =>
  formValues.costs.reduce((sum, entry) => sum + Number(entry.cost ?? 0), 0),
);

const getInitialValues = () => ({
  types: props.workshopAppointment?.types ?? [WorkshopAppointmentType.INSPECTION],
  costs: normalizeCosts(
    props.workshopAppointment?.types ?? [WorkshopAppointmentType.INSPECTION],
    props.workshopAppointment?.costs ?? [],
  ),
  startDate: props.workshopAppointment?.allocations?.[0]?.startDate ?? getDefaultStartDate(),
  endDate: props.workshopAppointment?.allocations?.[0]?.endDate ?? getDefaultEndDate(),
  allocationStatus:
    props.workshopAppointment?.allocations?.[0]?.status ?? AllocationStatus.CONFIRMED,
  locationType:
    props.workshopAppointment?.allocations?.[0]?.pickupLocationType ?? BookingLocationType.STATION,
  stationId: props.workshopAppointment?.allocations?.[0]?.pickupResponsibleStation?.id,
  location: {
    coordinates: pointToLocationDto(
      props.workshopAppointment?.allocations?.[0]?.pickupLocation ?? null,
    ),
    address: props.workshopAppointment?.allocations?.[0]?.pickupLocationGeocodedAddress ?? null,
  },
  contactPerson: props.workshopAppointment?.contactPerson ?? '',
  milageStart: workshopAllocation.value?.milageStart ?? null,
  milageEnd: workshopAllocation.value?.milageEnd ?? null,
  additionalInformation: props.workshopAppointment?.additionalInformation ?? '',
  usesCollectAndReturnService: props.workshopAppointment?.usesCollectAndReturnService ?? false,
  readyForPickup: props.workshopAppointment?.readyForPickup ?? false,
});
const {
  handleSubmit,
  values: formValues,
  resetForm,
  setFieldValue,
} = useForm<WorkshopAppointmentFormValues>({
  initialValues: getInitialValues(),
  validationSchema: toTypedSchema(
    yup.object({
      types: yup
        .array()
        .of(yup.mixed<WorkshopAppointmentType>().oneOf(Object.values(WorkshopAppointmentType)))
        .min(1)
        .required(),
      costs: yup
        .array()
        .of(
          yup.object({
            type: yup
              .mixed<WorkshopAppointmentType>()
              .oneOf(Object.values(WorkshopAppointmentType))
              .optional()
              .nullable(),
            cost: yup.number().min(0).optional().nullable(),
          }),
        )
        .optional(),
      startDate: dateOrStringRequired(),
      endDate: dateOrStringRequired(),
      allocationStatus: yup
        .mixed<AllocationStatus>()
        .oneOf(Object.values(AllocationStatus))
        .required(),
      locationType: yup
        .mixed<BookingLocationType>()
        .oneOf(Object.values(BookingLocationType))
        .optional(),
      stationId: yup.string().uuid().optional().nullable(),
      location: yup.mixed().optional().nullable(),
      contactPerson: yup.string().trim().max(255).optional(),
      milageStart: yup.number().min(0).optional().nullable(),
      milageEnd: yup.number().min(0).optional().nullable(),
      additionalInformation: yup.string().trim().max(2000).optional(),
      usesCollectAndReturnService: yup.boolean().required(),
      readyForPickup: yup.boolean().optional(),
    }),
  ),
});

const { canEditField, canEditResource } = useCanEditField(
  'Allocation',
  () => props.workshopAppointment?.allocations?.[0] ?? {},
  () => (props.workshopAppointment ? 'update' : 'create'),
);

const { canEditField: canEditWorkshopAppointmentField } = useCanEditField(
  'WorkshopAppointment',
  () => props.workshopAppointment ?? {},
  () => (props.workshopAppointment ? 'update' : 'create'),
);

watch(
  () => props.workshopAppointment,
  () => resetForm({ values: getInitialValues() }),
);

watch(
  () => formValues.types,
  (types) => {
    const normalizedCosts = normalizeCosts(types, formValues.costs);
    if (JSON.stringify(normalizedCosts) !== JSON.stringify(formValues.costs)) {
      setFieldValue('costs', normalizedCosts);
    }
  },
  { deep: true },
);

const submit = handleSubmit(async (values) => {
  const dto = buildWorkshopAppointmentDto(values);
  await saveWorkshopAppointment(dto);
});

const saveWorkshopAppointment = async (
  dto: CreateWorkshopAppointmentDto,
  skipCarAvailabilityCheck = false,
) => {
  const payload: CreateWorkshopAppointmentDto = {
    ...dto,
    ...(skipCarAvailabilityCheck ? { skipCarAvailabilityCheck: true } : {}),
  };

  try {
    if (props.workshopAppointment) {
      await updateWorkshopAppointment({
        id: props.workshopAppointment.id,
        dto: payload,
      });
      emit('complete');
      return;
    }

    const created = await createWorkshopAppointment(payload);
    emit('complete');
    const { isConfirmed } = await Alert.fire({
      titleText: t('appointmentCreated'),
      text: t('appointmentCreatedText'),
      icon: 'success',
      showCloseButton: true,
      confirmButtonText: t('viewAppointment'),
    });
    if (isConfirmed) {
      await router.push({ name: 'editWorkshopAppointment', params: { id: created.id } });
    }
  } catch (error) {
    if (!skipCarAvailabilityCheck && isCarUnavailableError(error)) {
      const { isConfirmed } = await Alert.fire({
        icon: 'warning',
        titleText: t('carUnavailableWarningTitle'),
        text: t('carUnavailableWarningText'),
        showCancelButton: true,
        confirmButtonText: t('saveAnyway'),
        cancelButtonText: t('cancel'),
      });
      if (isConfirmed) {
        await saveWorkshopAppointment(dto, true);
      }
      return;
    }
    throw error;
  }
};

const buildWorkshopAppointmentDto = (
  values: WorkshopAppointmentFormValues,
): CreateWorkshopAppointmentDto => {
  const startDate = new Date(values.startDate as string).toISOString();
  const endDate = new Date(values.endDate as string).toISOString();
  return {
    carId: props.car.id,
    types: values.types as WorkshopAppointmentType[],
    costs: normalizeCosts(values.types, values.costs).map((cost) => ({
      type: cost.type,
      cost: Number(cost.cost ?? 0),
    })),
    startDate: startDate,
    endDate: endDate,
    allocationStatus: values.allocationStatus,
    usesCollectAndReturnService: values.usesCollectAndReturnService,
    readyForPickup: values.readyForPickup,
    locationType: values.locationType,
    stationId:
      values.locationType === BookingLocationType.STATION
        ? values.stationId || undefined
        : undefined,
    location:
      values.locationType === BookingLocationType.STREET ? values.location?.coordinates : null,
    locationGeocodedAddress:
      values.locationType === BookingLocationType.STREET ? values.location?.address : null,
    contactPerson: values.contactPerson || undefined,
    additionalInformation: values.additionalInformation || undefined,
    ...(props.workshopAppointment
      ? {
          milageStart: values.milageStart,
          milageEnd: values.milageEnd,
        }
      : {}),
  };
};

const updateReadyForPickup = async (readyForPickup: boolean) => {
  setFieldValue('readyForPickup', readyForPickup);
  if (!props.workshopAppointment) return;
  await updateWorkshopAppointment({
    id: props.workshopAppointment.id,
    dto: { readyForPickup },
  });
};

const removeWorkshopAppointment = async () => {
  if (!props.workshopAppointment) return;
  const alertResult = await Alert.fire({
    titleText: t('reallyDeleteAppointmentTitle'),
    text: t('reallyDeleteAppointmentText'),
    icon: 'question',
    showConfirmButton: false,
    showDenyButton: true,
    denyButtonText: t('deleteAppointment'),
    showCancelButton: true,
    cancelButtonText: t('cancel'),
  });
  if (!alertResult.isDenied) return;
  await deleteWorkshopAppointment(props.workshopAppointment.id);
  emit('complete');
};
</script>

<i18n lang="json">
{
  "en": {
    "createWorkshopAppointment": "Create workshop appointment",
    "appointmentOverview": "Appointment overview",
    "types": "Appointment types",
    "selectTypes": "Select one or more types",
    "startDate": "Start date",
    "endDate": "End date",
    "allocationStatus": "Status",
    "location": "Location",
    "contactPerson": "Contact person",
    "handoverMileage": "Handover mileage",
    "returnMileage": "Return mileage",
    "costs": "Costs",
    "documents": "Documents",
    "totalCosts": "Total costs",
    "additionalInformation": "Additional information",
    "usesCollectAndReturnService": "Use collect and return service",
    "readyForPickup": "Vehicle ready for pickup (workshop finished)",
    "markAsReadyForPickup": "Mark as ready for pickup",
    "unsetReadyForPickup": "Mark as not ready for pickup",
    "createAppointment": "Create appointment",
    "saveAppointment": "Save appointment",
    "deleteAppointment": "Delete appointment",
    "appointmentCreated": "Appointment created",
    "appointmentCreatedText": "The workshop appointment was created successfully.",
    "viewAppointment": "View appointment",
    "reallyDeleteAppointmentTitle": "Really remove appointment?",
    "reallyDeleteAppointmentText": "The appointment and its related transfers will be permanently deleted.",
    "carUnavailableWarningTitle": "Car not available",
    "carUnavailableWarningText": "The car is actually not available during that period (rental, blocking, etc.). Do you still want to set the workshop appointment?",
    "saveAnyway": "Set workshop appointment anyway",
    "doesNotBlockBookings": "Does not block bookings",
    "cancel": "Cancel",
    "notSelected": "Not selected"
  },
  "de": {
    "createWorkshopAppointment": "Werkstatttermin erstellen",
    "appointmentOverview": "Terminübersicht",
    "types": "Terminarten",
    "selectTypes": "Eine oder mehrere Arten auswählen",
    "startDate": "Startdatum",
    "endDate": "Enddatum",
    "allocationStatus": "Status",
    "location": "Ort",
    "contactPerson": "Ansprechpartner",
    "handoverMileage": "Kilometerstand bei Übergabe",
    "returnMileage": "Kilometerstand bei Rückgabe",
    "costs": "Kosten",
    "documents": "Dokumente",
    "totalCosts": "Gesamtkosten",
    "additionalInformation": "Zusätzliche Informationen",
    "usesCollectAndReturnService": "Hol- und Bringservice nutzen",
    "readyForPickup": "Fahrzeug abholbereit",
    "markAsReadyForPickup": "Als abholbereit markieren",
    "unsetReadyForPickup": "Als nicht abholbereit markieren",
    "createAppointment": "Termin erstellen",
    "saveAppointment": "Termin aktualisieren",
    "deleteAppointment": "Termin löschen",
    "appointmentCreated": "Termin erstellt",
    "appointmentCreatedText": "Der Werkstatttermin wurde erfolgreich erstellt.",
    "viewAppointment": "Termin ansehen",
    "reallyDeleteAppointmentTitle": "Termin wirklich entfernen?",
    "reallyDeleteAppointmentText": "Der Termin und die zugehörigen Transfers werden unwiderruflich gelöscht.",
    "carUnavailableWarningTitle": "Fahrzeug nicht verfügbar",
    "carUnavailableWarningText": "Auto ist in dem Zeitraum eigentlich nicht verfügbar (Miete, Blockierung, etc.) Möchtest Du trotzdem den Werkstatttermin setzen?",
    "saveAnyway": "Trotzdem Werkstatttermin setzen",
    "doesNotBlockBookings": "Blockiert keine Buchungen",
    "cancel": "Abbrechen",
    "notSelected": "Nicht ausgewählt"
  },
  "es": {
    "createWorkshopAppointment": "Crear cita de taller",
    "appointmentOverview": "Resumen de la cita",
    "types": "Tipos de cita",
    "selectTypes": "Seleccionar uno o más tipos",
    "startDate": "Fecha de inicio",
    "endDate": "Fecha de fin",
    "allocationStatus": "Estado",
    "location": "Ubicación",
    "contactPerson": "Persona de contacto",
    "handoverMileage": "Kilometraje en la entrega",
    "returnMileage": "Kilometraje en la devolución",
    "costs": "Costes",
    "documents": "Documentos",
    "totalCosts": "Costes totales",
    "additionalInformation": "Información adicional",
    "usesCollectAndReturnService": "Usar servicio de recogida y entrega",
    "readyForPickup": "Vehículo listo para recogida (taller finalizado)",
    "markAsReadyForPickup": "Marcar como listo para recogida",
    "unsetReadyForPickup": "Marcar como no listo para recogida",
    "createAppointment": "Crear cita",
    "saveAppointment": "Guardar cita",
    "deleteAppointment": "Eliminar cita",
    "appointmentCreated": "Cita creada",
    "appointmentCreatedText": "La cita de taller se ha creado correctamente.",
    "viewAppointment": "Ver cita",
    "reallyDeleteAppointmentTitle": "¿Eliminar la cita?",
    "reallyDeleteAppointmentText": "La cita y sus traslados asociados se eliminarán de forma permanente.",
    "carUnavailableWarningTitle": "Vehículo no disponible",
    "carUnavailableWarningText": "El coche en realidad no está disponible en ese periodo (alquiler, bloqueo, etc.). ¿Quieres programar la cita de taller de todos modos?",
    "saveAnyway": "Programar cita de taller de todos modos",
    "doesNotBlockBookings": "No bloquea reservas",
    "cancel": "Cancelar",
    "notSelected": "No seleccionado"
  }
}
</i18n>
